import React, { useEffect, useState } from "react";
import api from "../../../../../api/api";
import {
  FaUserCircle,
} from "react-icons/fa";
import LoyaltyCard from "./LoyaltyCard";

// ============================================================
// ROYALTY LEVELS
// ============================================================

const ROYALTY_LEVELS = [
  50,
  150,
  300,
  600,
  1000,
];

// ============================================================
// FRONTEND POINT CALCULATION
// MUST MATCH RoyaltyEngine.calculate_points()
// ============================================================

export const calculatePoints = (amount) => {
  const value = Math.floor(
    Number(amount || 0)
  );

  if (value <= 0) {
    return 0;
  }

  const base = value;

  const bonus =
    Math.floor(value / 100) * 10;

  return base + bonus;
};

// ============================================================
// NEXT REWARD
// ============================================================

export const getNextReward = (points) => {
  const p = Number(points || 0);

  if (p < 50) {
    return "Reach 50 pts → $3 Voucher";
  }

  if (p < 150) {
    return "Reach 150 pts → $10 Discount Voucher";
  }

  if (p < 300) {
    return "Reach 300 pts → $25 Royalty Credit";
  }

  if (p < 600) {
    return "Reach 600 pts → $60 VIP Pack";
  }

  if (p < 1000) {
    return "Reach 1000 pts → $120 Elite Reward";
  }

  // ==========================================================
  // DYNAMIC REWARDS
  // ==========================================================

  const steps = Math.floor(
    (p - 1000) / 100
  );

  const rewardAmount =
    120 + steps * 5;

  const nextPoints =
    1000 + (steps + 1) * 100;

  const nextAmount =
    rewardAmount + 5;

  return `Reach ${nextPoints.toLocaleString()} pts → $${nextAmount} Elite Reward`;
};

// ============================================================
// PROGRESS
// ============================================================

export const getProgress = (points) => {
  const p = Number(points || 0);

  for (
    let i = 0;
    i < ROYALTY_LEVELS.length;
    i++
  ) {
    const level = ROYALTY_LEVELS[i];

    if (p < level) {
      const previous =
        ROYALTY_LEVELS[i - 1] || 0;

      return Math.min(
        100,
        Math.max(
          0,
          ((p - previous) /
            (level - previous)) *
            100
        )
      );
    }
  }

  return 100;
};

// ============================================================
// CUSTOMER ROYALTY
// ============================================================

const CustomerRoyalty = () => {
  const [customers, setCustomers] =
    useState([]);

  const [customer, setCustomer] =
    useState(null);

  const [totalCustomers, setTotalCustomers] =
    useState(0);

  const [view, setView] =
    useState("add");

  const [search, setSearch] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [amount, setAmount] =
    useState("");

  const [previewPoints, setPreviewPoints] =
    useState(0);

  const [newName, setNewName] =
    useState("");

  const [newPhone, setNewPhone] =
    useState("");

  const [newEmail, setNewEmail] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  // ==========================================================
  // NORMALIZE CUSTOMER
  // ==========================================================

  const normalizeCustomer = (
    data
  ) => {
    const points = Number(
      data?.reward_cycle_points ??
        data?.total_points ??
        data?.points ??
        0
    );

    return {
      ...data,

      total_points: points,

      reward_cycle_points: points,
    };
  };

  // ==========================================================
  // FETCH CUSTOMERS
  // ==========================================================

  const fetchCustomers = async (
    q = ""
  ) => {
    try {
      const res = await api.get(
        "/api/customers/",
        {
          params: {
            search: q,
          },
        }
      );

      const data = res.data;

      const list = Array.isArray(data)
        ? data
        : data?.results || [];

      setCustomers(
        list.map(normalizeCustomer)
      );
    } catch (error) {
      console.error(
        "FETCH CUSTOMERS ERROR:",
        error.response?.data || error
      );

      setCustomers([]);
    }
  };

  // ==========================================================
  // TOTAL CUSTOMERS
  // ==========================================================

  const fetchTotal = async () => {
    try {
      const res = await api.get(
        "/api/customers/total/"
      );

      setTotalCustomers(
        Number(
          res.data?.total_customers || 0
        )
      );
    } catch (error) {
      console.error(
        "FETCH TOTAL CUSTOMERS ERROR:",
        error.response?.data || error
      );
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    fetchCustomers();
    fetchTotal();
  }, []);

  // ==========================================================
  // SELECT CUSTOMER
  // ==========================================================

  const selectCustomer = async (
    c
  ) => {
    try {
      setLoading(true);

      const res = await api.get(
        `/api/customers/${c.id}/`
      );

      const normalized =
        normalizeCustomer(
          res.data
        );

      setCustomer(normalized);

      setView("purchase");

      setAmount("");

      setPreviewPoints(0);

      setMessage(
        `Customer ${normalized.full_name} selected`
      );
    } catch (error) {
      console.error(
        "SELECT CUSTOMER ERROR:",
        error.response?.data || error
      );

      setMessage(
        "Unable to load customer"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // ADD CUSTOMER
  // ==========================================================

  const addCustomer = async () => {
    if (
      !newName.trim() ||
      !newPhone.trim()
    ) {
      setMessage(
        "Name & phone are required"
      );

      return;
    }

    try {
      setLoading(true);

      const res = await api.post(
        "/api/customers/",
        {
          full_name:
            newName.trim(),

          phone:
            newPhone.trim(),

          email:
            newEmail.trim() ||
            null,
        }
      );

      const newCustomer =
        normalizeCustomer(
          res.data
        );

      setCustomers((prev) => [
        newCustomer,
        ...prev,
      ]);

      setCustomer(
        newCustomer
      );

      setView("purchase");

      setNewName("");
      setNewPhone("");
      setNewEmail("");

      setAmount("");
      setPreviewPoints(0);

      await fetchTotal();

      setMessage(
        "Customer created successfully"
      );
    } catch (error) {
      console.error(
        "ADD CUSTOMER ERROR:",
        error.response?.data ||
          error
      );

      const backendError =
        error.response?.data;

      if (backendError) {
        if (
          typeof backendError ===
          "object"
        ) {
          const messageText =
            Object.entries(
              backendError
            )
              .map(
                ([key, value]) =>
                  `${key}: ${
                    Array.isArray(value)
                      ? value.join(", ")
                      : value
                  }`
              )
              .join(" | ");

          setMessage(
            messageText
          );
        } else {
          setMessage(
            String(
              backendError
            )
          );
        }
      } else {
        setMessage(
          "Unable to create customer"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // AMOUNT CHANGE
  // ==========================================================

  const handleAmountChange = (
    e
  ) => {
    const value =
      e.target.value;

    setAmount(value);

    // IMPORTANT:
    // Use the same calculation
    // as RoyaltyEngine.calculate_points()

    const points =
      calculatePoints(
        value
      );

    setPreviewPoints(
      points
    );
  };

  // ==========================================================
  // PURCHASE
  // ==========================================================

  const handlePurchase = async () => {
    if (!customer) {
      setMessage(
        "Select a customer first"
      );

      return;
    }

    const numericAmount =
      Number(amount);

    if (
      !amount ||
      Number.isNaN(
        numericAmount
      ) ||
      numericAmount <= 0
    ) {
      setMessage(
        "Enter a valid purchase amount"
      );

      return;
    }

    try {
      setLoading(true);

      // ======================================================
      // SAVE THE CURRENT POINTS BEFORE PURCHASE
      // ======================================================

      const currentPoints =
        Number(
          customer.reward_cycle_points ??
            customer.total_points ??
            0
        );

      // ======================================================
      // CALCULATE EXPECTED POINTS
      // ======================================================

      const expectedPoints =
        calculatePoints(
          numericAmount
        );

      // ======================================================
      // POST PURCHASE
      // ======================================================

      const res =
        await api.post(
          "/api/royalty-purchases/",
          {
            customer:
              customer.id,

            amount:
              numericAmount,
          }
        );

      console.log(
        "PURCHASE RESPONSE:",
        res.data
      );

      // ======================================================
      // GET ACTUAL POINTS EARNED
      // ======================================================

      const pointsEarned =
        Number(
          res.data?.points_earned ??
            expectedPoints
        );

      // ======================================================
      // IMMEDIATELY UPDATE UI
      // ======================================================

      const estimatedNewPoints =
        currentPoints +
        pointsEarned;

      setCustomer(
        (prev) => ({
          ...prev,

          total_points:
            estimatedNewPoints,

          reward_cycle_points:
            estimatedNewPoints,
        })
      );

      // ======================================================
      // SUCCESS MESSAGE
      // ======================================================

      setMessage(
        `Purchase successful! +${pointsEarned.toLocaleString()} points earned.`
      );

      // ======================================================
      // CLEAR AMOUNT
      // ======================================================

      setAmount("");

      setPreviewPoints(0);

      // ======================================================
      // GET AUTHORITATIVE CUSTOMER DATA
      // ======================================================

      const updated =
        await api.get(
          `/api/customers/${customer.id}/`
        );

      const updatedCustomer =
        normalizeCustomer(
          updated.data
        );

      console.log(
        "UPDATED CUSTOMER:",
        updatedCustomer
      );

      // ======================================================
      // UPDATE CUSTOMER WITH DATABASE VALUE
      // ======================================================

      setCustomer(
        updatedCustomer
      );

      // ======================================================
      // REFRESH CUSTOMER LIST
      // ======================================================

      await fetchCustomers(
        search
      );
    } catch (error) {
      console.error(
        "PURCHASE ERROR:",
        error.response?.data ||
          error
      );

      const backendError =
        error.response?.data;

      setMessage(
        typeof backendError ===
          "object"
          ? Object.entries(
              backendError
            )
              .map(
                ([key, value]) =>
                  `${key}: ${
                    Array.isArray(value)
                      ? value.join(", ")
                      : value
                  }`
              )
              .join(" | ")
          : String(
              backendError ||
                "Purchase failed"
            )
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // CURRENT POINTS
  // ==========================================================

  const currentPoints =
    Number(
      customer?.reward_cycle_points ??
        customer?.total_points ??
        0
    );

  // ==========================================================
  // STYLES
  // ==========================================================

  const styles = {
    container: {
      display: "flex",
      minHeight: "100vh",
      fontFamily:
        "Arial, Helvetica, sans-serif",
      background:
        "linear-gradient(135deg,#eef2ff,#f8fafc)",
    },

    left: {
      width: "380px",
      background: "#ffffff",
      borderRight:
        "1px solid rgba(212,175,55,0.3)",
      padding: "18px",
      overflowY: "auto",
      boxSizing: "border-box",
    },

    right: {
      flex: 1,
      padding: "30px",
      overflowY: "auto",
      boxSizing: "border-box",
    },

    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#111827",
      marginBottom: "14px",
    },

    glassCard: {
      background: "#ffffff",
      borderRadius: "14px",
      padding: "18px",
      boxShadow:
        "0 10px 25px rgba(0,0,0,0.06)",
      marginBottom: "14px",
    },

    customerRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 12px",
      cursor: "pointer",
      transition:
        "all 0.25s ease",
      borderBottom:
        "1px solid rgba(212,175,55,0.25)",
      borderLeft:
        "3px solid transparent",
    },

    input: {
      width: "100%",
      padding: "12px",
      borderRadius: "10px",
      border:
        "1px solid #e5e7eb",
      marginBottom: "10px",
      boxSizing: "border-box",
      outline: "none",
      fontSize: "14px",
    },

    btn: {
      width: "100%",
      padding: "12px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontWeight: "600",
      background:
        "linear-gradient(135deg,#2563eb,#1d4ed8)",
      color: "white",
      fontSize: "14px",
    },

    backBtn: {
      padding: "10px 14px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      background: "#111827",
      color: "white",
      marginBottom: "12px",
    },

    badge: {
      padding: "12px",
      background: "#111827",
      color: "white",
      borderRadius: "10px",
      marginTop: "14px",
      fontSize: "14px",
    },

    progress: {
      height: "10px",
      background: "#e5e7eb",
      borderRadius: "20px",
      overflow: "hidden",
      marginTop: "10px",
    },

    progressFill: {
      height: "100%",
      background:
        "linear-gradient(90deg,#22c55e,#2563eb)",
      transition:
        "width 0.4s ease",
    },

    message: {
      background: "#fff",
      padding: "14px",
      borderRadius: "10px",
      marginTop: "15px",
      border:
        "1px solid #e5e7eb",
      color: "#374151",
      fontWeight: "600",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={
        styles.container
      }
    >

      {/* ======================================================
          LEFT SIDE
      ====================================================== */}

      <div style={styles.left}>

        <h2 style={styles.title}>
          🛒 Customers
        </h2>

        <input
          style={styles.input}
          placeholder="Search customer..."
          value={search}
          onChange={(e) => {
            const value =
              e.target.value;

            setSearch(value);

            fetchCustomers(
              value
            );
          }}
        />

        <div
          style={
            styles.glassCard
          }
        >
          <b>
            Total Customers:
          </b>{" "}
          {totalCustomers}
        </div>

        {customers.map(
          (c) => (
            <div
              key={c.id}
              style={
                styles.customerRow
              }
              onClick={() =>
                selectCustomer(c)
              }
              onMouseEnter={(
                e
              ) => {
                e.currentTarget.style.background =
                  "#F8F4E6";

                e.currentTarget.style.borderLeft =
                  "3px solid #D4AF37";

                e.currentTarget.style.paddingLeft =
                  "16px";
              }}
              onMouseLeave={(
                e
              ) => {
                e.currentTarget.style.background =
                  "transparent";

                e.currentTarget.style.borderLeft =
                  "3px solid transparent";

                e.currentTarget.style.paddingLeft =
                  "12px";
              }}
            >

              <FaUserCircle
                size={28}
                color="#B8860B"
              />

              <div>

                <b>
                  {c.full_name}
                </b>

                <div
                  style={{
                    fontSize: 12,
                    color:
                      "#6b7280",
                    marginTop: 3,
                  }}
                >
                  {c.phone}
                </div>

              </div>

            </div>
          )
        )}

      </div>

      {/* ======================================================
          RIGHT SIDE
      ====================================================== */}

      <div style={styles.right}>

        <h2 style={styles.title}>
          💼 Cashier Dashboard
        </h2>

        {/* ====================================================
            ADD CUSTOMER
        ==================================================== */}

        {view === "add" && (
          <div
            style={
              styles.glassCard
            }
          >

            <h3>
              Add Customer
            </h3>

            <input
              style={styles.input}
              placeholder="Full Name"
              value={newName}
              onChange={(e) =>
                setNewName(
                  e.target.value
                )
              }
            />

            <input
              style={styles.input}
              placeholder="Phone"
              value={newPhone}
              onChange={(e) =>
                setNewPhone(
                  e.target.value
                )
              }
            />

            <input
              style={styles.input}
              placeholder="Email"
              value={newEmail}
              onChange={(e) =>
                setNewEmail(
                  e.target.value
                )
              }
            />

            <button
              style={styles.btn}
              onClick={
                addCustomer
              }
              disabled={
                loading
              }
            >
              {loading
                ? "Creating..."
                : "➕ Add Customer"}
            </button>

          </div>
        )}

        {/* ====================================================
            CUSTOMER / ROYALTY
        ==================================================== */}

        {view === "purchase" &&
          customer && (
            <>
              <button
                style={
                  styles.backBtn
                }
                onClick={() => {
                  setView("add");
                  setCustomer(
                    null
                  );
                  setMessage("");
                  setAmount("");
                  setPreviewPoints(
                    0
                  );
                }}
              >
                ← Back
              </button>

              {/* ============================
                  ACTUAL ROYALTY CARD
              ============================ */}

              <LoyaltyCard
                customer={
                  customer
                }
              />

              {/* ============================
                  POINT BALANCE
              ============================ */}

              <div
                style={{
                  ...styles.glassCard,
                  textAlign:
                    "center",
                  marginTop:
                    "14px",
                }}
              >

                <div
                  style={{
                    fontSize:
                      "14px",
                    color:
                      "#6b7280",
                    marginBottom:
                      "5px",
                  }}
                >
                  CURRENT ROYALTY POINTS
                </div>

                <div
                  style={{
                    fontSize:
                      "42px",
                    fontWeight:
                      "800",
                    color:
                      "#111827",
                    lineHeight:
                      1.1,
                  }}
                >
                  {currentPoints.toLocaleString()}
                </div>

                <div
                  style={{
                    fontSize:
                      "14px",
                    color:
                      "#b8860b",
                    fontWeight:
                      "700",
                    marginTop:
                      "5px",
                  }}
                >
                  POINTS
                </div>

              </div>

              {/* ============================
                  PURCHASE
              ============================ */}

              <div
                style={
                  styles.glassCard
                }
              >

                <h3>
                  Make Purchase
                </h3>

                <input
                  style={
                    styles.input
                  }
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Purchase Amount"
                  value={amount}
                  onChange={
                    handleAmountChange
                  }
                />

                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  Expected Points:{" "}
                  <strong>
                    {previewPoints.toLocaleString()}
                  </strong>
                </div>

                <button
                  style={
                    styles.btn
                  }
                  onClick={
                    handlePurchase
                  }
                  disabled={
                    loading
                  }
                >
                  {loading
                    ? "Processing..."
                    : "Submit Purchase"}
                </button>

                <div
                  style={
                    styles.badge
                  }
                >
                  🎁{" "}
                  {getNextReward(
                    currentPoints
                  )}
                </div>

                <div
                  style={
                    styles.progress
                  }
                >
                  <div
                    style={{
                      ...styles.progressFill,

                      width: `${getProgress(
                        currentPoints
                      )}%`,
                    }}
                  />
                </div>

              </div>
            </>
          )}

        {/* ====================================================
            MESSAGE
        ==================================================== */}

        {message && (
          <div
            style={
              styles.message
            }
          >
            {message}
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomerRoyalty;